import { Head, Link, router, usePage } from "@inertiajs/react"
import { Building2, Plus } from "lucide-react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { ListPagination } from "@/components/admin/list-pagination"
import { ListShell } from "@/components/admin/list-shell"
import { NewBrandDialog } from "@/components/agency/new-brand-dialog"
import AgencySettingsLayout from "@/components/agency/settings/agency-settings-layout"
import { BrandLiveBadge } from "@/components/brand-live-badge"
import { EmptyState } from "@/components/empty-state"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { agencyBrandPath, agencySettingsBrandsPath } from "@/routes"
import type { SharedProps } from "@/types"
import type { AgencyBrandsIndexProps } from "@/types/agency"

/**
 * Agency/Brands/Index — paginated list of brands attached to the
 * current agency (OKS-13 BE TRD §API).
 *
 * OKS-854 — relocated from the top-level sidebar into Agency Settings.
 * Canonical address is now `/agency/settings/brands`; the old
 * `/agency/brands` 301s here. Only the shell changed — the query,
 * pagination, serializer and testid contract are untouched, so the locked
 * QA testids below still hold.
 *
 * Server-side this is a simple `Brand.where(agency_id: Current.user.
 * agency_id).order(:name).page(:page)` — no filters, no sort toggles in
 * v1. Pagination cursors via `?page=N`. Shared `<ListShell>` /
 * `<ListPagination>` chrome is reused from the admin surfaces so the
 * agency console feels like the platform — not a bolted-on alternate UI.
 *
 * Five UX states (per UX Spec §States):
 *   - empty (fresh) — `EmptyState` with the "Create your first brand"
 *     copy + invite to use the toolbar action
 *   - empty (filter miss) — N/A v1 (no filters)
 *   - loading — implicit during in-flight Inertia partial reload
 *   - error — read-side errors are vanishingly rare with Inertia's
 *     server-rendered model; no inline retry
 *   - offline — `<ListShell>`'s built-in `admin-offline-banner` via
 *     `useOnline`; pagination + dialog disable
 *
 * Per-row `BrandLiveBadge` renders inline with the brand name when
 * `live === false` (silent-norm: no badge for live brands). Same shared
 * primitive used on the admin side per OKS-13 F12 backport.
 *
 * Locked QA testid (OKS-13 v4 freeze):
 *   - `agency-brands-list` (page region)
 *   - `agency-list-row[data-brand-id]` per table row
 *   - `agency-new-brand-trigger` on the toolbar Button
 *   - `agency-brand-live-badge[data-state]` on the per-row badge
 *
 * Pagination: reuses `<ListPagination>` which carries the same
 * `admin-list-pagination*` testids — that contract is shared across both
 * admin and agency surfaces so QA's table fixtures stay one set.
 */
export default function AgencyBrandsIndex({
  brands,
  pagination,
}: AgencyBrandsIndexProps) {
  const { t, i18n } = useTranslation()
  const { props } = usePage<Partial<SharedProps>>()
  const agencyName = props.currentAccount?.name ?? ""

  const isEmpty = brands.length === 0

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  // Inertia partial reload — keep AgencyShell mounted, swap only the
  // list region. `router.get` with `only:["brands","pagination"]` is the
  // standard Inertia pattern for paginated lists.
  const onPageChange = useCallback(
    (page: number) =>
      router.get(
        agencySettingsBrandsPath(),
        { page },
        {
          preserveState: true,
          preserveScroll: true,
          only: ["brands", "pagination"],
        },
      ),
    [],
  )

  return (
    <AppLayout>
      <Head title={t("agency.brands.title")} />
      {/* OKS-854 — Brands now renders inside the Agency Settings shell
          (settings heading + sub-nav aside + content), `wide` so the brands
          data table keeps its legibility. Mirrors OKS-672's Team
          relocation; the page contents below are unchanged from the
          standalone version. */}
      <AgencySettingsLayout wide>
        <ListShell
          testId="agency-brands-list"
          title={t("agency.brands.title")}
          subtitle={t("agency.brands.subtitle_total", {
            count: pagination.total,
          })}
          action={
            <NewBrandDialog
              agencyName={agencyName}
              trigger={
                <Button type="button" data-testid="agency-new-brand-trigger">
                  <Plus className="size-4" aria-hidden="true" />
                  {t("agency.brands.dialog.new_brand.trigger")}
                </Button>
              }
            />
          }
          footer={
            isEmpty ? undefined : (
              <ListPagination
                page={pagination.page}
                perPage={pagination.per_page}
                total={pagination.total}
                onPageChange={onPageChange}
              />
            )
          }
        >
          {isEmpty ? (
            <EmptyState
              icon={Building2}
              title={t("agency.brands.empty_fresh.title")}
              description={t("agency.brands.empty_fresh.body")}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("agency.brands.column.name")}</TableHead>
                  <TableHead>{t("agency.brands.column.created_at")}</TableHead>
                  <TableHead>
                    <span className="sr-only">
                      {t("agency.brands.column.view")}
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brands.map((brand) => (
                  <TableRow
                    key={brand.id}
                    data-testid="agency-list-row"
                    data-brand-id={brand.id}
                  >
                    <TableCell className="font-medium">
                      <span className="inline-flex items-center gap-2">
                        <span>{brand.name}</span>
                        {/*
                         * `BrandLiveBadge` is a no-op for `live: true` so
                         * the table reads cleanly for the silent-norm
                         * majority. Override testid to the agency
                         * namespace per QA v4.
                         */}
                        <BrandLiveBadge
                          live={brand.live}
                          testId="agency-brand-live-badge"
                        />
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {dateFmt.format(new Date(brand.created_at))}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        href={agencyBrandPath({ id: brand.id })}
                        className="text-foreground hover:text-foreground/80 text-sm font-medium underline-offset-4 hover:underline"
                        aria-label={t("agency.brands.view_label", {
                          name: brand.name,
                        })}
                      >
                        {t("agency.brands.column.view")}
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ListShell>
      </AgencySettingsLayout>
    </AppLayout>
  )
}
